import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToHash } from "@/lib/scroll";
import { setInquiryPrefill } from "@/lib/inquiryPrefill";

const links = [
  { label: "Work", hash: "#work", testId: "nav-link-work" },
  { label: "Expertise", hash: "#expertise", testId: "nav-link-expertise" },
  { label: "The Edit Menu", hash: "#menu", testId: "nav-link-menu" },
  { label: "About", hash: "#about", testId: "nav-link-about" },
];

export const Nav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const go = (hash) => {
    setOpen(false);
    if (hash === "#inquire") {
      const projectTitle = document.querySelector("[data-project-title]")?.getAttribute("data-project-title");
      if (projectTitle) setInquiryPrefill(projectTitle);
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToHash(hash), 400);
    } else {
      setTimeout(() => scrollToHash(hash), 60);
    }
  };

  return (
    <>
      <header
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b border-[#f6f0e6]/10"
        style={{ background: "rgba(20, 10, 22, 0.72)" }}
      >
        <div className="flex items-center justify-between px-5 md:px-8 lg:px-24 h-14 md:h-20">
          <button
            data-testid="nav-wordmark"
            onClick={() => (location.pathname !== "/" ? navigate("/") : scrollToHash("#top"))}
            className="flex items-center gap-2.5 md:gap-3 font-serif text-base lg:text-lg xl:text-xl tracking-[0.08em] uppercase text-[#f6f0e6] hover:opacity-60 transition-opacity duration-300 whitespace-nowrap"
          >
            <img src="/brand/de-emblem-iridescent.png" alt="The Danielle Edit monogram" className="h-7 md:h-8 lg:h-9 w-auto" />
            <span className="whitespace-nowrap">The Danielle <span className="italic">Edit</span></span>
          </button>
          <nav className="flex items-center gap-5 md:gap-6 lg:gap-8 xl:gap-10">
            {links.map((l) => (
              <button
                key={l.hash}
                data-testid={l.testId}
                onClick={() => go(l.hash)}
                className="hidden md:block text-[10px] lg:text-[11px] uppercase tracking-[0.16em] lg:tracking-[0.22em] xl:tracking-[0.28em] text-[#efe6f4]/65 hover:text-[#f6f0e6] transition-colors duration-300 whitespace-nowrap"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-inquire-button"
              onClick={() => go("#inquire")}
              className="hidden md:block text-[10px] lg:text-[11px] uppercase tracking-[0.16em] lg:tracking-[0.28em] text-[#f6f0e6] border border-[#f6f0e6]/70 rounded-full px-4 lg:px-5 py-2 hover:bg-[#f6f0e6] hover:text-[#160a17] transition-colors duration-300 whitespace-nowrap"
            >
              Inquire
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="md:hidden p-2 -mr-2 text-[#f6f0e6]"
            >
              <Menu className="w-6 h-6" strokeWidth={1.25} />
            </button>
          </nav>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="fixed inset-0 z-[70] text-[#f6f0e6] flex flex-col md:hidden"
            style={{
              background:
                "radial-gradient(60% 40% at 85% 10%, rgba(122,79,208,0.22) 0%, transparent 65%), linear-gradient(168deg, #190b1c 0%, #100710 60%, #0b050f 100%)",
            }}
          >
            <div className="flex items-center justify-between px-5 h-14 border-b border-[#f6f0e6]/10">
              <span className="flex items-center gap-2.5 font-serif text-base tracking-[0.08em] uppercase whitespace-nowrap">
                <img src="/brand/de-emblem-iridescent.png" alt="" className="h-7 w-auto" />
                <span>The Danielle <span className="italic text-chrome-lavender">Edit</span></span>
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="p-2 -mr-2"
              >
                <X className="w-6 h-6" strokeWidth={1.25} />
              </button>
            </div>

            <nav className="flex-1 flex flex-col justify-center px-6 gap-2">
              {[...links, { label: "Inquire", hash: "#inquire", testId: "mobile-menu-link-inquire" }].map((l, i) => (
                <div key={l.hash} className="mask-line">
                  <motion.button
                    data-testid={l.testId.startsWith("mobile") ? l.testId : `mobile-menu-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.7, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    onClick={() => go(l.hash)}
                    className="block w-full text-left font-serif text-5xl py-3 tracking-tight hover:italic hover:text-chrome-lavender transition-colors duration-300"
                  >
                    <span className="text-xs font-sans tracking-[0.3em] text-[#f6f0e6]/40 align-super mr-4">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {l.label}
                  </motion.button>
                </div>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="px-6 pb-10"
            >
              <hr className="chrome-rule mb-6" />
              <div className="flex items-end justify-between">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#f6f0e6]/50">
                  by A Stephens Danielle Co.
                </p>
                <a
                  data-testid="mobile-menu-email-link"
                  href="mailto:dani@danielleedit.com"
                  className="font-serif italic text-lg"
                >
                  dani@danielleedit.com
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
